import type { Diff } from "@tiptap-pro/extension-snapshot-compare";
import type { Editor } from "@tiptap/core";
import {
  DOMSerializer,
  type Fragment,
  type Node as ProseMirrorNode,
} from "@tiptap/pm/model";
import { Decoration } from "@tiptap/pm/view";

const TABLE_ROW_NODE_TYPE = "tableRow";
const TABLE_CELL_NODE_TYPES = new Set(["tableCell", "tableHeader"]);
const TABLE_STRUCTURE_NODE_TYPES = new Set([
  "table",
  TABLE_ROW_NODE_TYPE,
  ...TABLE_CELL_NODE_TYPES,
]);

const isVersionComparisonTableStructureNodeType = (
  nodeTypeName?: string | null
) => Boolean(nodeTypeName && TABLE_STRUCTURE_NODE_TYPES.has(nodeTypeName));

const fragmentContainsVersionComparisonTableStructure = (
  fragment?: Fragment | null
) => {
  let containsTableStructure = false;

  fragment?.forEach((node) => {
    if (containsTableStructure) {
      return;
    }

    if (
      isVersionComparisonTableStructureNodeType(node.type.name) ||
      fragmentContainsVersionComparisonTableStructure(node.content)
    ) {
      containsTableStructure = true;
    }
  });

  return containsTableStructure;
};

export const shouldPreserveVersionComparisonTableNodeName = ({
  content,
  diffType,
  nodeTypeName,
}: {
  content?: Fragment | null;
  diffType?: string | null;
  nodeTypeName?: string | null;
}) =>
  (diffType === "block-insert" || diffType === "block-update") &&
  (isVersionComparisonTableStructureNodeType(nodeTypeName) ||
    fragmentContainsVersionComparisonTableStructure(content));

export const shouldRetainEmptyVersionComparisonTableParagraph = (
  parentNodeType?: string | null
) => Boolean(parentNodeType && TABLE_CELL_NODE_TYPES.has(parentNodeType));

const getDeletedTableNodes = (diff: Diff) => {
  if (diff.type !== "block-delete" || diff.content.childCount === 0) {
    return null;
  }

  const nodes: ProseMirrorNode[] = [];
  diff.content.forEach((node) => nodes.push(node));
  const containsOnlyRows = nodes.every(
    (node) => node.type.name === TABLE_ROW_NODE_TYPE
  );
  const containsOnlyCells = nodes.every((node) =>
    TABLE_CELL_NODE_TYPES.has(node.type.name)
  );

  return containsOnlyRows || containsOnlyCells ? nodes : null;
};

const applyDecorationAttributes = ({
  attributes,
  diff,
  element,
}: {
  attributes: Record<string, string | undefined>;
  diff: Diff;
  element: HTMLElement;
}) => {
  element.classList.add("diff", "delete", "block-delete");
  element.dataset.diffType = diff.type;

  if (diff.attribution.userId) {
    element.dataset.diffUserId = diff.attribution.userId;
  }

  Object.entries(attributes).forEach(([name, value]) => {
    if (value === undefined || name === "nodeName") {
      return;
    }

    if (name === "class") {
      value
        .split(/\s+/)
        .filter(Boolean)
        .forEach((className) => element.classList.add(className));
      return;
    }

    element.setAttribute(name, value);
  });
};

/**
 * Replaces only TipTap's invalid generic wrappers for deleted table rows and
 * cells. Returning null tells the caller to use the native decoration mapper.
 */
export const mapNativeTableDeletionDiffToDecorations = ({
  attributes,
  diff,
  editor,
}: {
  attributes: Record<string, string | undefined>;
  diff: Diff;
  editor: Editor;
}) => {
  const deletedTableNodes = getDeletedTableNodes(diff);

  if (!deletedTableNodes) {
    return null;
  }

  const serializer = DOMSerializer.fromSchema(editor.schema);

  return deletedTableNodes.flatMap((node) => {
    const element = serializer.serializeNode(node);

    if (!(element instanceof HTMLElement)) {
      return [];
    }

    applyDecorationAttributes({ attributes, diff, element });

    return [Decoration.widget(diff.from, element, { diff })];
  });
};
